import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CheckCircle, Phone, MapPin, Calendar, Download, Home } from "lucide-react";

const BookingSuccessPage = () => {
    const { bookingId } = useParams<{ bookingId: string }>();
    const navigate = useNavigate();

    return (
        <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 py-12">
            <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Success Header */}
                <div className="text-center mb-8">
                    <div className="flex justify-center mb-6">
                        <div className="relative">
                            <div className="absolute inset-0 bg-green-400 rounded-full opacity-25 animate-pulse"></div>
                            <CheckCircle className="w-20 h-20 text-green-600 relative" />
                        </div>
                    </div>
                    <h1 className="text-4xl font-bold text-gray-900 mb-2">
                        बुकिंग सफल! ✓
                    </h1>
                    <p className="text-xl text-gray-600">
                        आपकी लैब टेस्ट की बुकिंग पुष्टि हो गई है
                    </p>
                </div>

                {/* Booking Details Card */}
                <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {/* Left Column */}
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900 mb-6">बुकिंग विवरण</h2>
                            
                            <div className="space-y-4">
                                <div className="flex items-start gap-3">
                                    <div className="bg-blue-100 p-2 rounded-lg mt-1">
                                        <span className="text-blue-600 font-bold">📌</span>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-600">बुकिंग आईडी</p>
                                        <p className="font-semibold text-gray-900">{bookingId}</p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-3">
                                    <div className="bg-green-100 p-2 rounded-lg mt-1">
                                        <CheckCircle className="w-5 h-5 text-green-600" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-600">स्थिति</p>
                                        <p className="font-semibold text-green-600">✓ पुष्टि किया गया</p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-3">
                                    <div className="bg-purple-100 p-2 rounded-lg mt-1">
                                        <Phone className="w-5 h-5 text-purple-600" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-600">व्हाट्सएप पुष्टि</p>
                                        <p className="font-semibold text-gray-900">आपके फोन पर भेजी गई</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right Column */}
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900 mb-6">आगे की प्रक्रिया</h2>
                            
                            <div className="space-y-3">
                                <div className="flex gap-3 items-start">
                                    <div className="bg-yellow-100 text-yellow-700 rounded-full w-7 h-7 flex items-center justify-center flex-shrink-0 font-semibold text-sm">
                                        1
                                    </div>
                                    <div>
                                        <p className="font-medium text-gray-900">हमारी टीम आपसे संपर्क करेगी</p>
                                        <p className="text-sm text-gray-600">आपके दिए गए समय पर सैंपल एकत्र करने के लिए</p>
                                    </div>
                                </div>

                                <div className="flex gap-3 items-start">
                                    <div className="bg-blue-100 text-blue-700 rounded-full w-7 h-7 flex items-center justify-center flex-shrink-0 font-semibold text-sm">
                                        2
                                    </div>
                                    <div>
                                        <p className="font-medium text-gray-900">घर पर सैंपल संग्रह</p>
                                        <p className="text-sm text-gray-600">हमारे प्रशिक्षित फ़्लेबोटोमिस्ट आएंगे</p>
                                    </div>
                                </div>

                                <div className="flex gap-3 items-start">
                                    <div className="bg-green-100 text-green-700 rounded-full w-7 h-7 flex items-center justify-center flex-shrink-0 font-semibold text-sm">
                                        3
                                    </div>
                                    <div>
                                        <p className="font-medium text-gray-900">रिपोर्ट प्राप्त करें</p>
                                        <p className="text-sm text-gray-600">ईमेल और WhatsApp के माध्यम से</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Report Timeline Info */}
                <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-lg mb-8">
                    <h3 className="font-semibold text-blue-900 mb-2">⏱️ रिपोर्ट डिलीवरी समयरेखा</h3>
                    <p className="text-blue-800 text-sm">
                        आपकी रिपोर्ट सैंपल संग्रह के <strong>24-48 घंटे</strong> के भीतर प्रदान की जाएगी। आप अपनी रिपोर्ट ईमेल और व्हाट्सएप के माध्यम से प्राप्त करेंगे।
                    </p>
                </div>

                {/* Important Info */}
                <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-lg mb-8">
                    <h3 className="font-semibold text-amber-900 mb-3">⚠️ महत्वपूर्ण जानकारी</h3>
                    <ul className="text-amber-800 text-sm space-y-2">
                        <li>✓ अपने फोन पर WhatsApp पुष्टि संदेश की जांच करें</li>
                        <li>✓ यदि आपने उपवास की आवश्यकता हो तो निर्देश का पालन करें</li>
                        <li>✓ हमारी टीम 24 घंटों के भीतर संपर्क करेगी</li>
                        <li>✓ किसी भी प्रश्न के लिए +91 8302270668 पर कॉल करें</li>
                    </ul>
                </div>

                {/* Features */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                    <div className="bg-white rounded-lg p-6 text-center border">
                        <MapPin className="w-8 h-8 text-teal-600 mx-auto mb-3" />
                        <h4 className="font-semibold text-gray-900 mb-1">होम सर्विस</h4>
                        <p className="text-sm text-gray-600">अपने घर से सैंपल दें</p>
                    </div>

                    <div className="bg-white rounded-lg p-6 text-center border">
                        <Calendar className="w-8 h-8 text-blue-600 mx-auto mb-3" />
                        <h4 className="font-semibold text-gray-900 mb-1">तेज़ रिपोर्ट</h4>
                        <p className="text-sm text-gray-600">6-24 घंटों में उपलब्ध</p>
                    </div>

                    <div className="bg-white rounded-lg p-6 text-center border">
                        <Phone className="w-8 h-8 text-green-600 mx-auto mb-3" />
                        <h4 className="font-semibold text-gray-900 mb-1">24/7 समर्थन</h4>
                        <p className="text-sm text-gray-600">कभी भी पूछताछ करें</p>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-4">
                    <button
                        onClick={() => navigate("/lab-test")}
                        className="flex-1 bg-teal-600 text-white py-3 rounded-lg font-medium hover:bg-teal-700 transition-colors flex items-center justify-center gap-2"
                    >
                        <Home className="w-5 h-5" />
                        होम पेज पर जाएं
                    </button>
                    <button
                        onClick={() => window.print()}
                        className="flex-1 border-2 border-teal-600 text-teal-600 py-3 rounded-lg font-medium hover:bg-teal-50 transition-colors flex items-center justify-center gap-2"
                    >
                        <Download className="w-5 h-5" />
                        प्रिंट करें
                    </button>
                </div>

                {/* Footer Note */}
                <div className="text-center mt-8 text-sm text-gray-600">
                    <p>अपनी बुकिंग पूरी करने के लिए कृपया ऊपर दिए गए विकल्पों में से कोई एक चुनें</p>
                </div>
            </div>
        </div>
    );
};

export default BookingSuccessPage;
